import { create } from 'zustand';
import {
  DestinationFilter,
  ProvinceCreateModel,
  ProvinceModel,
  ProvinceUpdateModel,
} from '../model/destination.model';
import axiosInstance from '../../../configs/axios';
import { ApiPath } from '../../../constants/apiPath';
import { DestinationChildrenModel } from '../data/destination.model.child';
import { axiosErrorMessage } from '../../../utils/error-message';
import { AxiosError } from 'axios';

export interface ProvinceControllerState {
  list: ProvinceModel[];
  listCount: number;
  message: string | null;
  loading: boolean;
  filter: DestinationFilter | null;
  page: number;
  pageRowAmount: number;
  activeModal: DestinationChildrenModel | null;
  formUpdate: ProvinceUpdateModel | null;
}

export interface ProvinceControllerAction {
  getData: (startLoading?: boolean, filter?: DestinationFilter | null) => void;
  onPageChange: (page: number, rows: number) => void;
  onFilterChange: (filter?: DestinationFilter | null) => void;
  onHandleModal: (activeModal?: DestinationChildrenModel | null) => void;
  onUpdateFormUpdate: (form: ProvinceUpdateModel | null) => void;
  onSubmitCreate: (form: ProvinceCreateModel) => void;
  onSubmitUpdate: (form: ProvinceUpdateModel) => void;
  onDelete: (id: string) => void;
}

const ProvinceController = create<ProvinceControllerState & ProvinceControllerAction>()((set) => ({
  loading: false,
  list: [],
  message: null,
  listCount: 0,
  page: 0,
  pageRowAmount: 10,
  filter: { skip: 0, take: 10 },
  activeModal: null,
  formUpdate: null,

  getData: async (startLoading?: boolean, filter?: DestinationFilter | null) => {
    try {
      if (startLoading) set(() => ({ loading: true }));
      const res = await axiosInstance.get(ApiPath.provinces.all, {
        params: filter ?? { skip: 0, take: 10 },
      });
      const list: any = res?.data?.data ?? [];
      const listCount: any = res?.data?.meta?.total ?? res?.data?.count ?? 0;
      set(() => ({ list, listCount }));
    } catch (error) {
      set(() => ({ message: axiosErrorMessage({ error: error as AxiosError }) }));
    } finally {
      if (startLoading) setTimeout(() => set(() => ({ loading: false })), 500);
    }
  },

  onPageChange: (page: number, rows: number) => {
    set(() => ({ pageRowAmount: rows, page }));
  },

  onFilterChange(filter?: DestinationFilter | null) {
    set(() => ({ filter: filter ?? null }));
    this.getData(true, filter);
  },

  onHandleModal: (activeModal?: DestinationChildrenModel | null) => {
    set(() => ({ activeModal: activeModal ?? null, message: null }));
    if (!activeModal) set(() => ({ formUpdate: null }));
  },

  onUpdateFormUpdate: (form: ProvinceUpdateModel | null) => {
    set(() => ({ formUpdate: form, message: null }));
  },

  async onSubmitCreate(form: ProvinceCreateModel) {
    try {
      set(() => ({ loading: true, message: null }));
      const res = await axiosInstance.post(ApiPath.provinces.create, form);
      if (res?.status === 201) {
        set(() => ({ filter: null, page: 0 }));
        this.getData(true, null);
        setTimeout(() => this.onHandleModal(null), 500);
      } else {
        set(() => ({ message: res?.statusText }));
      }
    } catch (error) {
      set(() => ({ message: axiosErrorMessage({ error: error as AxiosError }) }));
    } finally {
      setTimeout(() => set(() => ({ loading: false })), 500);
    }
  },

  async onSubmitUpdate(form: ProvinceUpdateModel) {
    try {
      set(() => ({ loading: true, message: null }));
      const { id, uuid, ...body } = form;
      const res = await axiosInstance.patch(ApiPath.provinces.update(id ?? uuid ?? ''), body);
      if (res?.status === 200) {
        set(() => ({ formUpdate: null, filter: null, page: 0 }));
        this.getData(true, null);
        setTimeout(() => this.onHandleModal(null), 500);
      } else {
        set(() => ({ message: res?.statusText }));
      }
    } catch (error) {
      set(() => ({ message: axiosErrorMessage({ error: error as AxiosError }) }));
    } finally {
      setTimeout(() => set(() => ({ loading: false })), 500);
    }
  },

  async onDelete(id: string) {
    try {
      set(() => ({ loading: true }));
      const res = await axiosInstance.delete(ApiPath.provinces.delete(id));
      if (res?.status === 200) {
        this.getData(true, ProvinceController.getState().filter);
      } else {
        set(() => ({ message: res?.statusText }));
      }
    } catch (error) {
      set(() => ({ message: axiosErrorMessage({ error: error as AxiosError }) }));
    } finally {
      setTimeout(() => set(() => ({ loading: false })), 500);
    }
  },
}));

export default ProvinceController;
